import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useOrganization } from '../context/OrganizationContext';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';

import {
  Archive,
  ArrowLeft,
  ArrowsClockwise,
  Calendar,
  CheckCircle,
  CircleNotch,
  Clock,
  Eye,
  Star,
  TrendUp,
  Trophy
} from '@phosphor-icons/react';
const AssessmentHistory = () => {
  const navigate = useNavigate();
  const { activeOrganization, loading: orgLoading } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState([]);
  const [currentAssessmentId, setCurrentAssessmentId] = useState(null);
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [settingCurrent, setSettingCurrent] = useState(null);

  useEffect(() => {
    if (!activeOrganization || orgLoading) return;
    fetchHistory();
  }, [activeOrganization, orgLoading]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/assessments/history`,
        {},
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setAssessments(data.assessments || []);
        setCurrentAssessmentId(data.current_assessment_id);
      }
    } catch (error) {
      console.error('Error fetching assessment history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetAsCurrent = async (assessmentId, assessmentName) => {
    if (!window.confirm(`Set "${assessmentName}" as your current official assessment?`)) {
      return;
    }

    try {
      setSettingCurrent(assessmentId);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/assessments/${assessmentId}/set-current`,
        { method: 'POST' },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        await fetchHistory(); // ArrowsClockwise list
        alert('Assessment set as current successfully!');
      } else {
        throw new Error('Failed to set as current');
      }
    } catch (error) {
      console.error('Error setting current assessment:', error);
      alert('Failed to set assessment as current. Please try again.');
    } finally {
      setSettingCurrent(null);
    }
  };

  const handleCompare = async () => {
    if (selectedForComparison.length !== 2) {
      alert('Please select exactly 2 assessments to compare');
      return;
    }

    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/assessments/compare?assessment_id_1=${selectedForComparison[0]}&assessment_id_2=${selectedForComparison[1]}`,
        {},
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setComparisonData(data);
        setShowCompareModal(true);
      }
    } catch (error) {
      console.error('Error comparing assessments:', error);
      alert('Failed to compare assessments');
    }
  };

  const toggleSelectForComparison = (assessmentId) => {
    if (selectedForComparison.includes(assessmentId)) {
      setSelectedForComparison(selectedForComparison.filter(id => id !== assessmentId));
    } else if (selectedForComparison.length < 2) {
      setSelectedForComparison([...selectedForComparison, assessmentId]);
    } else {
      alert('You can only compare 2 assessments at a time');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-[#14F195]';
      case 'in-progress': return 'text-[#F59E0B]';
      default: return 'text-[#A0AEC0]';
    }
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'from-[#14F195] to-[#10B981]';
      case 'B': return 'from-[#00E5C0] to-[#00D4B0]';
      case 'C': return 'from-[#F59E0B] to-[#F97316]';
      case 'D': return 'from-[#EF4444] to-[#DC2626]';
      default: return 'from-[#A0AEC0] to-[#718096]';
    }
  };

  if (loading || orgLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch size={48} className="animate-spin text-[#00E5C0]" />
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-12">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-[#A0AEC0] hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          
          <h1 className="text-4xl font-bold text-white mb-2">
            Assessment History
          </h1>
          <p className="text-[#A0AEC0]">
            Track your organization&apos;s AI readiness progress over time
          </p>
        </div>

        {/* Compare Button */}
        {selectedForComparison.length === 2 && (
          <div className="mb-6">
            <button
              onClick={handleCompare}
              className="px-6 py-3 rounded-full bg-[#00E5C0] hover:bg-[#00D4B0] text-white font-semibold transition-colors"
            >
              <ArrowsClockwise size={20} className="inline mr-2" />
              Compare Selected Assessments
            </button>
          </div>
        )}

        {/* Assessments List */}
        <div className="space-y-4">
          {assessments.length === 0 ? (
            <div className="glass rounded-3xl p-12 text-center">
              <p className="text-[#A0AEC0] mb-4">No assessments found</p>
              <button
                onClick={() => navigate('/assessments/wizard')}
                className="px-6 py-3 rounded-full bg-[#00E5C0] hover:bg-[#00D4B0] text-white font-semibold transition-colors"
              >
                Create First Assessment
              </button>
            </div>
          ) : (
            assessments.map((assessment) => (
              <motion.div
                key={assessment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`glass rounded-2xl p-6 ${
                  selectedForComparison.includes(assessment.id) ? 'ring-2 ring-[#00E5C0]' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    {/* Select for Comparison */}
                    <button
                      onClick={() => toggleSelectForComparison(assessment.id)}
                      className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                        selectedForComparison.includes(assessment.id)
                          ? 'bg-[#00E5C0] border-[#00E5C0]'
                          : 'border-[#718096] hover:border-[#00E5C0]'
                      }`}
                    >
                      {selectedForComparison.includes(assessment.id) && (
                        <CheckCircle size={14} className="text-white" />
                      )}
                    </button>

                    {/* Assessment Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white">
                          {assessment.assessment_name || assessment.company_name}
                        </h3>
                        
                        {assessment.id === currentAssessmentId && (
                          <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#14F195] to-[#10B981] text-white text-xs font-bold">
                            <Star size={12} className="inline mr-1" />
                            CURRENT
                          </span>
                        )}
                        
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(assessment.status)}`}>
                          {assessment.status === 'completed' ? (
                            <><CheckCircle size={12} className="inline mr-1" />Completed</>
                          ) : (
                            <><Clock size={12} className="inline mr-1" />In Progress</>
                          )}
                        </span>

                        {assessment.is_archived && (
                          <span className="px-3 py-1 rounded-full bg-[#A0AEC0]/20 text-[#A0AEC0] text-xs font-semibold">
                            <Archive size={12} className="inline mr-1" />
                            Archived
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-6 text-sm text-[#A0AEC0]">
                        <div className="flex items-center gap-2">
                          <Calendar size={16} />
                          {new Date(assessment.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <TrendUp size={16} />
                          Score: {assessment.ai_readiness_score}/100
                        </div>

                        <div className={`flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r ${getGradeColor(assessment.readiness_grade)}`}>
                          <Trophy size={16} className="text-white" />
                          <span className="text-white font-bold">Grade {assessment.readiness_grade}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`/assessments/results/${assessment.id}`)}
                      className="px-4 py-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all"
                    >
                      <Eye size={16} className="inline mr-2" />
                      View
                    </button>

                    {assessment.status === 'completed' && assessment.id !== currentAssessmentId && !assessment.is_archived && (
                      <button
                        onClick={() => handleSetAsCurrent(assessment.id, assessment.assessment_name || assessment.company_name)}
                        disabled={settingCurrent === assessment.id}
                        className="px-4 py-2 rounded-full bg-[#00E5C0] hover:bg-[#00D4B0] text-white font-semibold disabled:opacity-50 transition-all"
                      >
                        {settingCurrent === assessment.id ? (
                          <><CircleNotch size={16} className="inline mr-2 animate-spin" />Setting...</>
                        ) : (
                          <><Star size={16} className="inline mr-2" />Set as Current</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Comparison Modal */}
      {showCompareModal && comparisonData && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-3xl font-bold text-white mb-6">Assessment Comparison</h2>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Assessment 1 */}
              <div className="glass rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">
                  {comparisonData.assessment_1.assessment_name || 'Assessment 1'}
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-[#A0AEC0] text-sm">Date</div>
                    <div className="text-white font-semibold">
                      {new Date(comparisonData.assessment_1.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#A0AEC0] text-sm">Score</div>
                    <div className="text-white font-bold text-2xl">
                      {comparisonData.assessment_1.ai_readiness_score}/100
                    </div>
                  </div>
                  <div>
                    <div className="text-[#A0AEC0] text-sm">Grade</div>
                    <div className={`inline-flex px-3 py-1 rounded-full bg-gradient-to-r ${getGradeColor(comparisonData.assessment_1.readiness_grade)} text-white font-bold`}>
                      {comparisonData.assessment_1.readiness_grade}
                    </div>
                  </div>
                </div>
              </div>

              {/* Assessment 2 */}
              <div className="glass rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">
                  {comparisonData.assessment_2.assessment_name || 'Assessment 2'}
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-[#A0AEC0] text-sm">Date</div>
                    <div className="text-white font-semibold">
                      {new Date(comparisonData.assessment_2.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#A0AEC0] text-sm">Score</div>
                    <div className="text-white font-bold text-2xl">
                      {comparisonData.assessment_2.ai_readiness_score}/100
                    </div>
                  </div>
                  <div>
                    <div className="text-[#A0AEC0] text-sm">Grade</div>
                    <div className={`inline-flex px-3 py-1 rounded-full bg-gradient-to-r ${getGradeColor(comparisonData.assessment_2.readiness_grade)} text-white font-bold`}>
                      {comparisonData.assessment_2.readiness_grade}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Delta */}
            <div className="glass rounded-2xl p-6 mb-6">
              <h3 className="text-xl font-bold text-white mb-4">Change Analysis</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <div className="text-[#A0AEC0] text-sm mb-2">Score Change</div>
                  <div className={`text-2xl font-bold ${
                    comparisonData.comparison.score_delta > 0 ? 'text-[#14F195]' :
                    comparisonData.comparison.score_delta < 0 ? 'text-[#EF4444]' : 'text-white'
                  }`}>
                    {comparisonData.comparison.score_delta > 0 ? '+' : ''}
                    {comparisonData.comparison.score_delta} points
                  </div>
                  <div className="text-[#A0AEC0] text-sm">
                    ({comparisonData.comparison.score_delta_percentage > 0 ? '+' : ''}
                    {comparisonData.comparison.score_delta_percentage}%)
                  </div>
                </div>

                <div>
                  <div className="text-[#A0AEC0] text-sm mb-2">Grade Change</div>
                  <div className="text-white font-bold text-2xl">
                    {comparisonData.comparison.grade_changed ? (
                      comparisonData.comparison.grade_improvement ? (
                        <span className="text-[#14F195]">↑ Improved</span>
                      ) : (
                        <span className="text-[#EF4444]">↓ Declined</span>
                      )
                    ) : (
                      'No Change'
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-[#A0AEC0] text-sm mb-2">Time Between</div>
                  <div className="text-white font-bold text-2xl">
                    {comparisonData.comparison.days_between_assessments} days
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowCompareModal(false)}
              className="px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold"
            >
              Close
            </button>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default AssessmentHistory;
